/* eslint-disable */
// ID card renderer, same as the admin panel copy (public/js/idcard-render.js).
/*
 * ID card renderer (shared by the admin panel and the React website).
 * Draws a CR80-size (85.6 x 54 mm, ~300 dpi) card on a <canvas>.
 *
 *   PTIdCard.draw(canvas, data, qrcodeFactory) -> Promise
 *   PTIdCard.download(canvas, fileName)
 *
 * data: { title, tagline, footer, logo, photo, name, designation, card_no,
 *         mobile, address, issued_at, valid_till, verify_url }
 * logo / photo must be data: URIs (or same-origin URLs) so the canvas stays exportable.
 * qrcodeFactory is the "qrcode-generator" function (window.qrcode or the npm import).
 */
const PTIdCard = (function () {

    var W = 1012;
    var H = 638;
    var ORANGE = '#f57c00';
    var ORANGE_DARK = '#e65100';
    var NAVY = '#1b2a4a';
    var TEXT = '#1f2933';
    var MUTED = '#6b7280';
    var FONT = "'Segoe UI', 'Nirmala UI', 'Noto Sans Devanagari', 'Noto Sans', Arial, sans-serif";

    function font(size, weight) {
        return (weight || 'normal') + ' ' + size + 'px ' + FONT;
    }

    function loadImage(src) {
        return new Promise(function (resolve) {
            if (!src) {
                resolve(null);
                return;
            }
            var img = new Image();
            img.onload = function () { resolve(img); };
            img.onerror = function () { resolve(null); };
            img.src = src;
        });
    }

    function roundRect(ctx, x, y, w, h, r) {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
    }

    /** Draws an image to fill the box (crop to cover). */
    function drawCover(ctx, img, x, y, w, h) {
        var scale = Math.max(w / img.width, h / img.height);
        var sw = w / scale;
        var sh = h / scale;
        var sx = (img.width - sw) / 2;
        var sy = (img.height - sh) / 2;
        ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
    }

    /** Largest font size (<= max) that fits the width on one line. */
    function fitSize(ctx, text, maxWidth, max, min, weight) {
        for (var size = max; size > min; size--) {
            ctx.font = font(size, weight);
            if (ctx.measureText(text).width <= maxWidth) {
                return size;
            }
        }
        return min;
    }

    /** Splits text into at most maxLines lines that fit maxWidth (adds "..." if cut). */
    function wrap(ctx, text, maxWidth, maxLines) {
        var words = String(text || '').replace(/\s+/g, ' ').trim().split(' ');
        var lines = [];
        var line = '';

        for (var i = 0; i < words.length; i++) {
            var word = words[i];
            var test = line ? line + ' ' + word : word;
            if (ctx.measureText(test).width <= maxWidth) {
                line = test;
                continue;
            }
            if (line) {
                lines.push(line);
            }
            // A single word longer than the line: cut it by characters.
            while (ctx.measureText(word).width > maxWidth && word.length > 1) {
                var cut = word.length - 1;
                while (cut > 1 && ctx.measureText(word.slice(0, cut)).width > maxWidth) {
                    cut--;
                }
                lines.push(word.slice(0, cut));
                word = word.slice(cut);
            }
            line = word;
        }
        if (line) {
            lines.push(line);
        }

        if (lines.length > maxLines) {
            lines = lines.slice(0, maxLines);
            var last = lines[maxLines - 1];
            while (last.length > 1 && ctx.measureText(last + '...').width > maxWidth) {
                last = last.slice(0, -1);
            }
            lines[maxLines - 1] = last + '...';
        }
        return lines;
    }

    function formatDate(value) {
        var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
        return m ? m[3] + '-' + m[2] + '-' + m[1] : '';
    }

    function drawQr(ctx, qrcodeFactory, text, x, y, size) {
        ctx.fillStyle = '#ffffff';
        roundRect(ctx, x, y, size, size, 12);
        ctx.fill();
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 2;
        ctx.stroke();

        if (!qrcodeFactory || !text) {
            return;
        }
        var qr = qrcodeFactory(0, 'M');
        qr.addData(text);
        qr.make();

        var count = qr.getModuleCount();
        var pad = 10;
        var cell = Math.floor((size - pad * 2) / count);
        var offset = Math.floor((size - cell * count) / 2);

        ctx.fillStyle = '#000000';
        for (var r = 0; r < count; r++) {
            for (var c = 0; c < count; c++) {
                if (qr.isDark(r, c)) {
                    ctx.fillRect(x + offset + c * cell, y + offset + r * cell, cell, cell);
                }
            }
        }
    }

    function field(ctx, label, value, x, y, maxWidth, maxLines) {
        ctx.fillStyle = MUTED;
        ctx.font = font(17, '600');
        ctx.fillText(label.toUpperCase(), x, y);

        ctx.fillStyle = TEXT;
        ctx.font = font(25, '600');
        var lines = wrap(ctx, value || '-', maxWidth, maxLines || 1);
        for (var i = 0; i < lines.length; i++) {
            ctx.fillText(lines[i], x, y + 30 + i * 30);
        }
        return y + 30 + (lines.length - 1) * 30;
    }

    function draw(canvas, data, qrcodeFactory) {
        data = data || {};
        canvas.width = W;
        canvas.height = H;

        var ready = (typeof document !== 'undefined' && document.fonts && document.fonts.ready)
            ? document.fonts.ready.catch(function () {})
            : Promise.resolve();

        return ready.then(function () {
            return Promise.all([loadImage(data.logo), loadImage(data.photo)]);
        }).then(function (images) {
            var logo = images[0];
            var photo = images[1];
            var ctx = canvas.getContext('2d');

            ctx.clearRect(0, 0, W, H);
            ctx.save();
            roundRect(ctx, 0, 0, W, H, 28);
            ctx.clip();

            // Background
            // Staff cards use a navy theme, member cards an orange theme, so they are easy to tell apart.
            var isStaff = data.holder_type === 'staff';
            var theme = isStaff
                ? { head1: '#1b2a4a', head2: '#2f4f86', stripe: ORANGE, accent: NAVY, pill: ORANGE, foot: ORANGE, footText: '#ffffff', footLabel: '#1b2a4a', tint: 'rgba(27, 42, 74, 0.06)', label: 'STAFF IDENTITY CARD', badge: 'STAFF' }
                : { head1: '#ff9800', head2: ORANGE_DARK, stripe: NAVY, accent: ORANGE, pill: NAVY, foot: NAVY, footText: '#ffffff', footLabel: '#ffb74d', tint: 'rgba(245, 124, 0, 0.06)', label: 'MEMBER IDENTITY CARD', badge: 'MEMBER' };

            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, W, H);
            ctx.fillStyle = theme.tint;
            ctx.beginPath();
            ctx.arc(W - 60, H - 120, 260, 0, Math.PI * 2);
            ctx.fill();

            // Header band
            var grad = ctx.createLinearGradient(0, 0, W, 0);
            grad.addColorStop(0, theme.head1);
            grad.addColorStop(1, theme.head2);
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, W, 150);
            ctx.fillStyle = theme.stripe;
            ctx.fillRect(0, 150, W, 6);

            // Card type badge, top right of the header
            ctx.font = font(22, 'bold');
            var badgeW = ctx.measureText(theme.badge).width + 36;
            roundRect(ctx, W - 40 - badgeW, 22, badgeW, 40, 20);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.fillStyle = isStaff ? NAVY : ORANGE_DARK;
            ctx.textAlign = 'center';
            ctx.fillText(theme.badge, W - 40 - badgeW / 2, 50);
            ctx.textAlign = 'left';

            var textX = 40;
            if (logo) {
                ctx.save();
                ctx.beginPath();
                ctx.arc(95, 75, 56, 0, Math.PI * 2);
                ctx.fillStyle = '#ffffff';
                ctx.fill();
                ctx.beginPath();
                ctx.arc(95, 75, 51, 0, Math.PI * 2);
                ctx.clip();
                drawCover(ctx, logo, 44, 24, 102, 102);
                ctx.restore();
                textX = 170;
            }

            var title = String(data.title || '');
            var titleWidth = W - textX - 40 - badgeW - 16;
            ctx.fillStyle = '#ffffff';
            ctx.font = font(fitSize(ctx, title, titleWidth, 44, 24, 'bold'), 'bold');
            ctx.textBaseline = 'alphabetic';
            ctx.fillText(title, textX, data.tagline ? 70 : 90);
            if (data.tagline) {
                ctx.font = font(fitSize(ctx, String(data.tagline), titleWidth, 26, 16, '600'), '600');
                ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
                ctx.fillText(String(data.tagline), textX, 112);
            }

            // Photo
            var px = 40, py = 186, pw = 226, ph = 290;
            ctx.save();
            roundRect(ctx, px, py, pw, ph, 16);
            ctx.fillStyle = '#f3f4f6';
            ctx.fill();
            ctx.clip();
            if (photo) {
                drawCover(ctx, photo, px, py, pw, ph);
            }
            ctx.restore();
            roundRect(ctx, px, py, pw, ph, 16);
            ctx.strokeStyle = theme.accent;
            ctx.lineWidth = 5;
            ctx.stroke();

            // QR
            var qrSize = 176;
            var qrX = W - 40 - qrSize;
            var qrY = 186;
            drawQr(ctx, qrcodeFactory, data.verify_url, qrX, qrY, qrSize);
            ctx.fillStyle = MUTED;
            ctx.font = font(17, '600');
            ctx.textAlign = 'center';
            ctx.fillText('Scan to verify', qrX + qrSize / 2, qrY + qrSize + 28);
            if (data.issued_at) {
                ctx.fillText('Issued: ' + formatDate(data.issued_at), qrX + qrSize / 2, qrY + qrSize + 54);
            }
            ctx.textAlign = 'left';

            // Text fields
            var fx = px + pw + 34;
            var fw = qrX - 24 - fx;
            var name = String(data.name || '');
            ctx.fillStyle = NAVY;
            var nameSize = fitSize(ctx, name, fw, 40, 26, 'bold');
            ctx.font = font(nameSize, 'bold');
            var nameLines = wrap(ctx, name, fw, 2);
            var y = 222;
            for (var i = 0; i < nameLines.length; i++) {
                ctx.fillText(nameLines[i], fx, y + i * (nameSize + 6));
            }
            y += (nameLines.length - 1) * (nameSize + 6);

            // Designation under the name (e.g. Editor, District Coordinator, Member)
            var designation = String(data.designation || (isStaff ? 'Staff' : 'Member'));
            ctx.fillStyle = theme.accent === NAVY ? '#2f4f86' : ORANGE_DARK;
            ctx.font = font(fitSize(ctx, designation, fw, 28, 18, 'bold'), 'bold');
            ctx.fillText(designation, fx, y + 40);
            y += 40;

            ctx.fillStyle = theme.accent;
            ctx.fillRect(fx, y + 14, 70, 4);

            var gap = 34;
            y = field(ctx, 'ID No.', data.card_no, fx, y + 48, fw, 1);
            y = field(ctx, 'Mobile', data.mobile, fx, y + gap, fw, 1);
            y = field(ctx, 'Address', data.address, fx, y + gap, fw, nameLines.length > 1 ? 1 : 2);
            if (data.valid_till) {
                field(ctx, 'Valid till', formatDate(data.valid_till), fx, y + gap, fw, 1);
            }

            // Footer band
            ctx.fillStyle = theme.foot;
            ctx.fillRect(0, H - 58, W, 58);
            ctx.fillStyle = theme.footText;
            ctx.font = font(fitSize(ctx, String(data.footer || ''), W - 420, 22, 14, '600'), '600');
            ctx.fillText(String(data.footer || ''), 40, H - 21);
            ctx.fillStyle = theme.footLabel;
            ctx.font = font(22, 'bold');
            ctx.textAlign = 'right';
            ctx.fillText(theme.label, W - 40, H - 21);
            ctx.textAlign = 'left';

            ctx.restore();
            return canvas;
        });
    }

    function download(canvas, fileName) {
        var name = fileName || 'id-card.png';
        if (canvas.toBlob) {
            canvas.toBlob(function (blob) {
                var url = URL.createObjectURL(blob);
                var a = document.createElement('a');
                a.href = url;
                a.download = name;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(function () { URL.revokeObjectURL(url); }, 2000);
            }, 'image/png');
        } else {
            var a = document.createElement('a');
            a.href = canvas.toDataURL('image/png');
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a.remove();
        }
    }

    return { draw: draw, download: download, width: W, height: H };

})();

export default PTIdCard;
